import { uniqBy } from "lodash";

const INITIAL_COMMENTS_PREVIEW_AMOUNT = 2;
const INITIAL_REPLIES_PREVIEW_AMOUNT = 0;

export const author = (item = {}) => ({
  id: item.id,
  userId: item.id,
  name: item.data?.name,
  avatar: item.data?.avatar || "",
  type: item.data?.type,
  jobTitle: item.data?.title || "",
  companyName: item.data?.company || "",
  location: item.data?.location || "",
  pronouns: item.data?.pronouns || "",
  displayPronouns: item.data?.displayPronouns || false,
});

export const reactionCounts = (counts) => {
  const reactionCountsObj = { ...counts };
  delete reactionCountsObj.comment;
  delete reactionCountsObj.reply;
  delete reactionCountsObj.answer;
  return reactionCountsObj;
};

export const reaction = (item) => ({
  item,
  type: item.kind, // todo: deprecate. confusing with reducer action "type"
  kind: item.kind,
  id: item.id,
});

export const reactions = (items = {}) => {
  const types = Object.keys(items);
  const formatted = [];
  types.forEach((type) => {
    if (type === "comment") return;
    if (type === "reply") return;
    if (type === "answer") return;
    if (type === "vote") return;
    items[type].forEach((r) => formatted.push(reaction(r)));
  });
  return formatted;
};

export const votes = (items = {}) => {
  const types = Object.keys(items);
  const formatted = [];
  types.forEach((type) => {
    if (type !== "vote") return;
    items[type].forEach((r) => formatted.push(reaction(r)));
  });
  return formatted;
};

export const reply = (item, index, previewOnly) => ({
  id: item.id,
  author: author(item.user),
  body: item.data.body,
  edited: item.data.edited,
  mediaUrl: item.data.mediaUrl,
  mediaType: item.data.mediaType,
  item,
  createdAt: item.created_at,
  reactionCounts: reactionCounts(item.children_counts),
  reactions: reactions(item.latest_children),
  isHidden: previewOnly && index > INITIAL_REPLIES_PREVIEW_AMOUNT,
});

export const comment = (item) => ({
  id: item?.id,
  author: author(item.user),
  body: item.data?.body ?? item.data?.answer,
  edited: item.data.edited,
  mediaUrl: item.data.mediaUrl,
  mediaType: item.data.mediaType,
  createdAt: item.created_at,
  item,
  reactionCounts: reactionCounts(item.children_counts),
  reactions: reactions(item.latest_children),
  replyCount: item.children_counts.reply || 0,
  replies:
    item.latest_children.reply
      ?.slice(0, INITIAL_REPLIES_PREVIEW_AMOUNT)
      .map(reply) || [],
});

export const comments = (items = {}, ignorePreviewAmount) => {
  const itemsComments = ignorePreviewAmount
    ? items.comment
    : items.comment?.slice(0, INITIAL_COMMENTS_PREVIEW_AMOUNT);

  return itemsComments?.map((c, index) => comment(c, index, !index)) || [];
};

export const answers = (items = {}, ignorePreviewAmount) => {
  const itemsAnswers = ignorePreviewAmount
    ? items.answer
    : items.answer?.slice(0, INITIAL_COMMENTS_PREVIEW_AMOUNT);

  return itemsAnswers?.map((c, index) => comment(c, index, !index)) || [];
};

export default function activityFormatter(activity) {
  const isAggregated = Boolean(activity.activities);

  const time = activity?.activities?.[0]?.time || activity?.time;

  const createdAt = `${time}Z`;

  const isEdited =
    ["comment", "event", "poll", "post", "reply"].includes(activity?.verb) &&
    (activity.activities?.[0]?.object?.updated_at !==
      activity.activities?.[0]?.object?.created_at ||
      activity?.object?.updated_at !== activity?.object?.created_at);

  const formattedActivity = {
    id: activity.id,
    createdAt,
    type: activity.verb,
    isAggregated,
    item: activity,
    isNew: true,
    edited: isEdited,
    score: activity?.score || 0,
  };

  const formatPost = (postActivity, commentOverride) => {
    const post = {
      id: postActivity.id,
      aggregateId: isAggregated ? activity.id : undefined,
      author: author(postActivity.actor),
      reactionCounts: reactionCounts(postActivity.reaction_counts),
      reactions: reactions(postActivity.latest_reactions),
      commentCount: postActivity.reaction_counts?.comment || 0,
      comments: comments(
        commentOverride || postActivity.latest_reactions,
        Boolean(commentOverride)
      ),
      answerCount: postActivity.reaction_counts?.answer || 0,
      answers: answers(
        commentOverride || postActivity.latest_reactions,
        Boolean(commentOverride)
      ),
      ownAnswers: answers(
        commentOverride || postActivity.own_reactions,
        Boolean(commentOverride)
      ),
      votesCount: postActivity.reaction_counts?.vote || 0,
      votes: votes(postActivity.latest_reactions),
      ownVotes: votes(postActivity.own_reactions),
      body: postActivity.object.data?.body,
    };

    if (postActivity.mediaType && postActivity.mediaUrl) {
      post.mediaType = postActivity.mediaType;
      post.mediaUrl = postActivity.mediaUrl;
    }

    if (postActivity.groupRef) {
      post.postedTo = {
        group: postActivity.groupRef,
        type: "group",
      };
    } else {
      post.postedTo = { label: postActivity.audience || "general" };
    }

    return post;
  };

  const activityItem = isAggregated ? activity.activities[0] : activity;
  Object.assign(formattedActivity, formatPost(activityItem));

  const formatActors = () => {
    return isAggregated
      ? uniqBy(
          activity.activities.map((a) => author(a.actor)),
          "userId"
        )
      : [author(activity.actor)];
  };

  if (formattedActivity.type === "follow") {
    formattedActivity.target = {
      avatar: activityItem.object?.data?.avatar,
      company: activityItem.object?.data?.company,
      id: activityItem.object?.id,
      name: activityItem.object?.data?.name,
      title: activityItem.object?.data?.title,
      type: "user",
    };
    formattedActivity.actors = formatActors();
  }

  if (formattedActivity.type === "join") {
    formattedActivity.target = {
      channelId: activityItem.object?.data?.channelId,
      createdAt: activityItem.object?.created_at,
      image: activityItem.object?.data?.image,
      name: activityItem.object?.data?.name,
      type: activityItem.object?.collection,
    };
    formattedActivity.actors = formatActors();
  }

  if (
    formattedActivity.type === "thirdparty" ||
    formattedActivity.type === "expertpanel" ||
    formattedActivity.type === "article"
  ) {
    formattedActivity.target = {
      url: activityItem.object?.data?.url,
      editorsChoice: activityItem.object?.data?.editorsChoice,
    };
    formattedActivity.actors = formatActors();
  }

  if (
    formattedActivity.type === "react" ||
    formattedActivity.type === "comment"
  ) {
    formattedActivity.actors = formatActors();
    formattedActivity.target = activityItem.object.id;
  }
  return formattedActivity;
}
